import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { QrCode, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const invitedEmail = params.get("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(invitedEmail || "");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    const ok = await register(email.trim(), password, name.trim(), inviteToken);
    setLoading(false);
    if (ok) {
      toast.success(
        inviteToken
          ? "Account created! You've joined the team."
          : `Welcome to Qdurator, ${name.split(" ")[0] || "friend"}!`
      );
      navigate("/dashboard");
    } else {
      setErr("Could not create account. That email may already be registered.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="max-w-md mx-auto w-full px-4 sm:px-6 flex-1 flex flex-col justify-center py-12">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8" data-testid="register-logo">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow">
            <QrCode className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-black text-2xl tracking-tight text-slate-900">Qdurator</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="register-heading">
            {inviteToken ? "Join your team on Qdurator" : "Create your account"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {inviteToken ? "Set up your account to accept the invite." : "Start tracking QR scans in minutes."}
          </p>

          {inviteToken && invitedEmail && (
            <div className="mt-4 text-xs bg-blue-50 border border-blue-200 text-blue-800 px-3 py-2 rounded-lg" data-testid="register-invite-banner">
              You're being added as a team member on the account tied to <b>{invitedEmail}</b>.
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Business or your name
              </Label>
              <Input
                data-testid="register-name-input"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Inc."
                className="h-11 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Email
              </Label>
              <Input
                data-testid="register-email-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-11 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Password
              </Label>
              <Input
                data-testid="register-password-input"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="h-11 rounded-xl"
              />
            </div>

            {err && (
              <div
                data-testid="register-error"
                className="text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Create account <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-blue-600 hover:text-blue-700 font-semibold"
              data-testid="register-goto-login"
            >
              Log in
            </Link>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
